<template>
  <a-modal
    :title="designerVisible ? '模板设置 - ' + billTypeName : title"
    :visible="visible"
    :width="designerVisible ? 1200 : 520"
    :footer="null"
    :maskClosable="false"
    wrapClassName="dining-print-modal"
    @cancel="handleCancel">
    <!-- 预览模式工具栏 -->
    <div v-if="!designerVisible" class="print-toolbar">
      <a-button icon="setting" @click="handleTemplateSetting">模板设置</a-button>
      <a-button icon="eye" @click="handlePreviewPrint">预览打印</a-button>
      <a-button type="primary" icon="printer" :loading="loading" @click="handlePrint">打印</a-button>
    </div>
    <!-- 小票预览区 -->
    <a-spin v-if="!designerVisible" :spinning="loading" tip="正在加载打印数据...">
      <div class="preview-area">
        <div ref="printContent" class="print-content" :style="canvasStyle"></div>
      </div>
    </a-spin>
    <!-- 内嵌模板设计器 -->
    <div v-else class="designer-area">
      <EmbeddedDesigner
        :billType="billTypeCode"
        :billTypeName="billTypeName"
        :billNo="printData ? printData.orderNo : ''"
        :templateId="templateId"
        :billData="renderData"
        @close="onDesignerClose"
        @saved="onDesignerSaved" />
    </div>
  </a-modal>
</template>

<script>
import { orderPrintData, receiptPrint } from '@/api/plugin/dining'
import { loadTemplateWithFallback, getCurrentSystemInfo } from '@/api/plugin/print-template'
import HiprintService from '@/services/hiprint-service'
import EmbeddedDesigner from '@/views/plugin/print-template/EmbeddedDesigner.vue'

// 打印类型 → 单据类型映射（billType 为模板存取键）
const PRINT_TYPE_MAP = {
  pre_settle: { billType: 'YJD', billTypeName: '预结单' },
  settle: { billType: 'JZD', billTypeName: '结账单' }
}

// 组件内置兜底模板（与 jsh_print_template tenant_id=0 系统模板同源 JSON，逐字一致）
const FALLBACK_TEMPLATES = {
  YJD: JSON.parse('{"panels":[{"index":0,"name":1,"height":296.6,"width":58,"paperHeader":15,"paperFooter":364.22764227642284,"printElements":[{"options":{"left":1.5,"top":22.5,"height":13,"width":135,"testData":"@门店名称","hCenter":29.0,"bottom":35.5,"coordinateSync":false,"right":136.5,"title":"门店名称","qrCodeLevel":0,"qid":"storeName","field":"storeName","widthHeightSync":false,"vCenter":69.0,"hideTitle":true,"fontSize":11.25,"lineHeight":14.25},"printElementType":{"title":"门店名称","type":"text"}},{"options":{"left":3,"top":43.5,"height":9,"width":132,"hCenter":48.0,"borderWidth":"0.75","bottom":52.5,"coordinateSync":false,"right":135,"widthHeightSync":false,"vCenter":69.0,"borderStyle":"dashed"},"printElementType":{"title":"横线","type":"hline"}},{"options":{"left":0,"top":49.5,"height":13,"width":133.5,"testData":"@桌台","hCenter":56.0,"bottom":62.5,"coordinateSync":false,"right":133.5,"title":"桌台","qrCodeLevel":0,"qid":"tableName","field":"tableName","widthHeightSync":false,"vCenter":66.75},"printElementType":{"title":"桌台","type":"text"}},{"options":{"left":0,"top":63,"height":13,"width":133.5,"testData":"@人数","hCenter":69.5,"bottom":76,"coordinateSync":false,"right":133.5,"title":"人数","qrCodeLevel":0,"qid":"personCount","field":"personCount","widthHeightSync":false,"vCenter":66.75},"printElementType":{"title":"人数","type":"text"}},{"options":{"left":0,"top":76.5,"height":13,"width":135,"testData":"@订单号","hCenter":83.0,"bottom":89.5,"coordinateSync":false,"right":135,"title":"订单号","qrCodeLevel":0,"qid":"orderNo","field":"orderNo","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"订单号","type":"text"}},{"options":{"left":0,"top":90,"height":13,"width":132,"testData":"@单据日期","hCenter":96.5,"bottom":103,"coordinateSync":false,"right":132,"title":"单据日期","qrCodeLevel":0,"qid":"operTimeStr","field":"operTimeStr","widthHeightSync":false,"vCenter":66.0},"printElementType":{"title":"单据日期","type":"text"}},{"options":{"left":1.5,"top":108,"height":9,"width":132,"hCenter":112.5,"borderWidth":"0.75","bottom":117,"coordinateSync":false,"right":133.5,"widthHeightSync":false,"vCenter":67.5,"borderStyle":"dashed"},"printElementType":{"title":"横线","type":"hline"}},{"options":{"left":-3,"top":117,"height":57,"width":144,"tableFooterRepeat":"last","hCenter":145.5,"bottom":174,"coordinateSync":false,"tableFooterCellBorder":"noBorder","tableBodyRowBorder":"noBorder","tableHeaderBorder":"noBorder","right":141,"qid":"table","tableFooterBorder":"noBorder","field":"table","tableBorder":"noBorder","tableHeaderCellBorder":"noBorder","columns":[[{"width":58.21936630811994,"title":"品名","field":"name","checked":true,"columnId":"name","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":true,"tableSummary":""},{"width":27.791426847309943,"title":"数量","field":"quantity","checked":true,"columnId":"quantity","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":false,"tableSummary":"sum","tableSummaryAlign":"left"},{"width":26.001830707696957,"title":"单价","field":"unitPrice","checked":true,"columnId":"unitPrice","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0},{"width":31.987376136873173,"title":"小计","field":"allPrice","checked":true,"columnId":"allPrice","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":false,"tableSummary":"sum","tableSummaryAlign":"left"}]],"lineHeight":13.5,"widthHeightSync":false,"vCenter":69.0,"tableBodyCellBorder":"noBorder"},"printElementType":{"title":"表格信息","type":"table","editable":true,"columnDisplayEditable":true,"columnDisplayIndexEditable":true,"columnTitleEditable":true,"columnResizable":true,"columnAlignEditable":true,"isEnableEditField":true,"isEnableContextMenu":true,"isEnableInsertRow":true,"isEnableDeleteRow":true,"isEnableInsertColumn":true,"isEnableDeleteColumn":true,"isEnableMergeCell":true}},{"options":{"left":0,"top":184.5,"height":13,"width":135,"testData":"@合计","hCenter":191.0,"bottom":197.5,"coordinateSync":false,"right":135,"title":"合计","qrCodeLevel":0,"qid":"totalAmount","field":"totalAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"合计","type":"text"}},{"options":{"left":1.5,"top":198.0,"height":13,"width":132,"testData":"@服务费","hCenter":204.5,"bottom":211.0,"coordinateSync":false,"right":133.5,"title":"服务费","qrCodeLevel":0,"qid":"serviceAmount","field":"serviceAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"服务费","type":"text"}},{"options":{"left":1.5,"top":211.5,"height":13,"width":132,"testData":"@餐位费","hCenter":218.0,"bottom":224.5,"coordinateSync":false,"right":133.5,"title":"餐位费","qrCodeLevel":0,"qid":"tablewareAmount","field":"tablewareAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"餐位费","type":"text"}},{"options":{"left":1.5,"top":225.0,"height":13,"width":132,"testData":"@会员优惠","hCenter":231.5,"bottom":238.0,"coordinateSync":false,"right":133.5,"title":"会员优惠","qrCodeLevel":0,"qid":"discountAmount","field":"discountAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"会员优惠","type":"text"}},{"options":{"left":1.5,"top":238.5,"height":13,"width":132,"testData":"@抹零","hCenter":245.0,"bottom":251.5,"coordinateSync":false,"right":133.5,"title":"抹零","qrCodeLevel":0,"qid":"eraseAmount","field":"eraseAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"抹零","type":"text"}},{"options":{"left":1.5,"top":252.0,"height":13,"width":132,"testData":"@订金","hCenter":258.5,"bottom":265.0,"coordinateSync":false,"right":133.5,"title":"订金","qrCodeLevel":0,"qid":"depositAmount","field":"depositAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"订金","type":"text"}},{"options":{"left":1.5,"top":265.5,"height":13,"width":132,"testData":"@应付","hCenter":272.0,"bottom":278.5,"coordinateSync":false,"right":133.5,"title":"应付","qrCodeLevel":0,"qid":"grossPayable","field":"grossPayable","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"应付","type":"text"}},{"options":{"left":1.5,"top":282,"height":20,"width":130.5,"hCenter":292.0,"bottom":302,"coordinateSync":false,"right":132.0,"widthHeightSync":false,"title":"谢谢惠顾，欢迎下次光临！","vCenter":66.75,"qrCodeLevel":0},"printElementType":{"title":"文本","type":"text"}}],"paperNumberLeft":81,"paperNumberTop":374,"paperNumberDisabled":true,"paperNumberContinue":true,"panelPageRule":"none","scale":1.2000000000000002,"watermarkOptions":{"rotate":25,"width":200,"format":"YYYY-MM-DD HH:mm","fillStyle":"rgba(184, 184, 184, 0.3)","fontSize":"14px","content":"","height":200,"timestamp":false},"panelLayoutOptions":{"layoutColumnGap":0,"layoutType":"column","layoutRowGap":0}}]}'),
  JZD: JSON.parse('{"panels":[{"index":0,"name":1,"height":296.6,"width":58,"paperHeader":15,"paperFooter":364.22764227642284,"printElements":[{"options":{"left":1.5,"top":22.5,"height":13,"width":135,"testData":"@门店名称","hCenter":29.0,"bottom":35.5,"coordinateSync":false,"right":136.5,"title":"门店名称","qrCodeLevel":0,"qid":"storeName","field":"storeName","widthHeightSync":false,"vCenter":69.0,"hideTitle":true,"fontSize":11.25,"lineHeight":14.25},"printElementType":{"title":"门店名称","type":"text"}},{"options":{"left":3,"top":43.5,"height":9,"width":132,"hCenter":48.0,"borderWidth":"0.75","bottom":52.5,"coordinateSync":false,"right":135,"widthHeightSync":false,"vCenter":69.0,"borderStyle":"dashed"},"printElementType":{"title":"横线","type":"hline"}},{"options":{"left":0,"top":49.5,"height":13,"width":133.5,"testData":"@桌台","hCenter":56.0,"bottom":62.5,"coordinateSync":false,"right":133.5,"title":"桌台","qrCodeLevel":0,"qid":"tableName","field":"tableName","widthHeightSync":false,"vCenter":66.75},"printElementType":{"title":"桌台","type":"text"}},{"options":{"left":0,"top":63,"height":13,"width":133.5,"testData":"@人数","hCenter":69.5,"bottom":76,"coordinateSync":false,"right":133.5,"title":"人数","qrCodeLevel":0,"qid":"personCount","field":"personCount","widthHeightSync":false,"vCenter":66.75},"printElementType":{"title":"人数","type":"text"}},{"options":{"left":0,"top":76.5,"height":13,"width":135,"testData":"@订单号","hCenter":83.0,"bottom":89.5,"coordinateSync":false,"right":135,"title":"订单号","qrCodeLevel":0,"qid":"orderNo","field":"orderNo","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"订单号","type":"text"}},{"options":{"left":0,"top":90,"height":13,"width":132,"testData":"@单据日期","hCenter":96.5,"bottom":103,"coordinateSync":false,"right":132,"title":"单据日期","qrCodeLevel":0,"qid":"operTimeStr","field":"operTimeStr","widthHeightSync":false,"vCenter":66.0},"printElementType":{"title":"单据日期","type":"text"}},{"options":{"left":1.5,"top":108,"height":9,"width":132,"hCenter":112.5,"borderWidth":"0.75","bottom":117,"coordinateSync":false,"right":133.5,"widthHeightSync":false,"vCenter":67.5,"borderStyle":"dashed"},"printElementType":{"title":"横线","type":"hline"}},{"options":{"left":-3,"top":117,"height":57,"width":144,"tableFooterRepeat":"last","hCenter":145.5,"bottom":174,"coordinateSync":false,"tableFooterCellBorder":"noBorder","tableBodyRowBorder":"noBorder","tableHeaderBorder":"noBorder","right":141,"qid":"table","tableFooterBorder":"noBorder","field":"table","tableBorder":"noBorder","tableHeaderCellBorder":"noBorder","columns":[[{"width":58.21936630811994,"title":"品名","field":"name","checked":true,"columnId":"name","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":true,"tableSummary":""},{"width":27.791426847309943,"title":"数量","field":"quantity","checked":true,"columnId":"quantity","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":false,"tableSummary":"sum","tableSummaryAlign":"left"},{"width":26.001830707696957,"title":"单价","field":"unitPrice","checked":true,"columnId":"unitPrice","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0},{"width":31.987376136873173,"title":"小计","field":"allPrice","checked":true,"columnId":"allPrice","fixed":false,"rowspan":1,"colspan":1,"tableQRCodeLevel":0,"tableSummaryTitle":false,"tableSummary":"sum","tableSummaryAlign":"left"}]],"lineHeight":13.5,"widthHeightSync":false,"vCenter":69.0,"tableBodyCellBorder":"noBorder"},"printElementType":{"title":"表格信息","type":"table","editable":true,"columnDisplayEditable":true,"columnDisplayIndexEditable":true,"columnTitleEditable":true,"columnResizable":true,"columnAlignEditable":true,"isEnableEditField":true,"isEnableContextMenu":true,"isEnableInsertRow":true,"isEnableDeleteRow":true,"isEnableInsertColumn":true,"isEnableDeleteColumn":true,"isEnableMergeCell":true}},{"options":{"left":0,"top":184.5,"height":13,"width":135,"testData":"@合计","hCenter":191.0,"bottom":197.5,"coordinateSync":false,"right":135,"title":"合计","qrCodeLevel":0,"qid":"totalAmount","field":"totalAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"合计","type":"text"}},{"options":{"left":1.5,"top":198.0,"height":13,"width":132,"testData":"@服务费","hCenter":204.5,"bottom":211.0,"coordinateSync":false,"right":133.5,"title":"服务费","qrCodeLevel":0,"qid":"serviceAmount","field":"serviceAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"服务费","type":"text"}},{"options":{"left":1.5,"top":211.5,"height":13,"width":132,"testData":"@餐位费","hCenter":218.0,"bottom":224.5,"coordinateSync":false,"right":133.5,"title":"餐位费","qrCodeLevel":0,"qid":"tablewareAmount","field":"tablewareAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"餐位费","type":"text"}},{"options":{"left":1.5,"top":225.0,"height":13,"width":132,"testData":"@会员优惠","hCenter":231.5,"bottom":238.0,"coordinateSync":false,"right":133.5,"title":"会员优惠","qrCodeLevel":0,"qid":"discountAmount","field":"discountAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"会员优惠","type":"text"}},{"options":{"left":1.5,"top":238.5,"height":13,"width":132,"testData":"@抹零","hCenter":245.0,"bottom":251.5,"coordinateSync":false,"right":133.5,"title":"抹零","qrCodeLevel":0,"qid":"eraseAmount","field":"eraseAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"抹零","type":"text"}},{"options":{"left":1.5,"top":252.0,"height":13,"width":132,"testData":"@订金","hCenter":258.5,"bottom":265.0,"coordinateSync":false,"right":133.5,"title":"订金","qrCodeLevel":0,"qid":"depositAmount","field":"depositAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"订金","type":"text"}},{"options":{"left":1.5,"top":265.5,"height":13,"width":132,"testData":"@应付","hCenter":272.0,"bottom":278.5,"coordinateSync":false,"right":133.5,"title":"应付","qrCodeLevel":0,"qid":"grossPayable","field":"grossPayable","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"应付","type":"text"}},{"options":{"left":1.5,"top":279.0,"height":13,"width":132,"testData":"@实付","hCenter":285.5,"bottom":292.0,"coordinateSync":false,"right":133.5,"title":"实付","qrCodeLevel":0,"qid":"payAmount","field":"payAmount","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"实付","type":"text"}},{"options":{"left":1.5,"top":292.5,"height":13,"width":132,"testData":"@支付方式","hCenter":299.0,"bottom":305.5,"coordinateSync":false,"right":133.5,"title":"支付方式","qrCodeLevel":0,"qid":"payType","field":"payType","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"支付方式","type":"text"}},{"options":{"left":1.5,"top":306.0,"height":13,"width":132,"testData":"@结算时间","hCenter":312.5,"bottom":319.0,"coordinateSync":false,"right":133.5,"title":"结算时间","qrCodeLevel":0,"qid":"settleTime","field":"settleTime","widthHeightSync":false,"vCenter":67.5},"printElementType":{"title":"结算时间","type":"text"}},{"options":{"left":1.5,"top":322.5,"height":20,"width":130.5,"hCenter":332.5,"bottom":342.5,"coordinateSync":false,"right":132.0,"widthHeightSync":false,"title":"谢谢惠顾，欢迎下次光临！","vCenter":66.75,"qrCodeLevel":0},"printElementType":{"title":"文本","type":"text"}}],"paperNumberLeft":81,"paperNumberTop":374,"paperNumberDisabled":true,"paperNumberContinue":true,"panelPageRule":"none","scale":1.2000000000000002,"watermarkOptions":{"rotate":25,"width":200,"format":"YYYY-MM-DD HH:mm","fillStyle":"rgba(184, 184, 184, 0.3)","fontSize":"14px","content":"","height":200,"timestamp":false},"panelLayoutOptions":{"layoutColumnGap":0,"layoutType":"column","layoutRowGap":0}}]}')
}

const PrintModal = {
  name: 'DiningPrintModal',
  components: {
    EmbeddedDesigner
  },
  data() {
    return {
      visible: false,
      loading: false,
      title: '打印预览',
      billTypeCode: '',
      billTypeName: '',
      printData: null,
      templateJson: null,
      templateId: null,
      designerVisible: false
    }
  },
  computed: {
    // 传给设计器/渲染的数据：模板明细表格 field 为 table，补 items 别名
    renderData() {
      return this.printData ? this.buildRenderData(this.printData) : null
    },
    // 预览画布尺寸 = 模板 panel 纸张尺寸（renderHtml 取 paper innerHTML，画布需复刻纸张宽高）
    canvasStyle() {
      const panel = this.templateJson && this.templateJson.panels && this.templateJson.panels[0]
      if (!panel) {
        return {}
      }
      return {
        position: 'relative',
        width: (Number(panel.width) || 58) + 'mm',
        minHeight: (Number(panel.height) || 296.6) + 'mm'
      }
    }
  },
  methods: {
    /**
     * 打开预览弹窗：取数 → 加载模板 → hiprint 渲染预览
     * @param {number|string} orderId 订单 id
     * @param {string} printType 'pre_settle'（预结单/YJD）| 'settle'（结账单/JZD）
     */
    async show(orderId, printType) {
      const conf = PRINT_TYPE_MAP[printType]
      if (!orderId || !conf) {
        console.warn('[DiningPrintModal] 无效参数:', orderId, printType)
        return
      }
      this.orderId = orderId
      this.printType = printType
      this.billTypeCode = conf.billType
      this.billTypeName = conf.billTypeName
      this.title = conf.billTypeName + '打印'
      this.printData = null
      this.templateJson = null
      this.templateId = null
      this.designerVisible = false
      this.visible = true
      this.loading = true
      try {
        const data = await this.fetchPrintData(orderId, printType)
        if (!data) {
          this.visible = false
          return
        }
        this.printData = data
        this.templateJson = await this.loadTemplate(this.billTypeCode)
        this.$nextTick(() => this.renderPreview())
      } catch (error) {
        console.error('[DiningPrintModal] show error:', error)
        this.$message.error('获取打印数据失败，请重试')
        this.visible = false
      } finally {
        this.loading = false
      }
    },
    /**
     * 静默打印（Task 9 切后端通道）：不弹窗，POST /print/receipt/{orderId}
     * 由后端 ESC/POS 渲染直打收银台打印机（网关/云盒通道），不再走浏览器 hiprint；
     * 下发即成功回执（实际出票结果由 print_log/重投调度兜底），错误仅提示不上抛。
     */
    async printSilent(orderId, printType) {
      const conf = PRINT_TYPE_MAP[printType]
      if (!orderId || !conf) {
        console.warn('[DiningPrintModal] 无效参数:', orderId, printType)
        return
      }
      try {
        const res = await receiptPrint(orderId, { printType })
        if (res.code === 200) {
          this.$message.success(conf.billTypeName + '已下发打印')
        } else {
          this.$message.error(res.message || '打印下发失败')
        }
      } catch (error) {
        console.error('[DiningPrintModal] printSilent error:', error)
        this.$message.error('打印下发失败，请重试')
      }
    },
    /**
     * 调 orderPrintData 取打印数据
     * @returns {Promise<Object|null>} printData，失败返回 null（内部已 message 提示）
     */
    async fetchPrintData(orderId, printType) {
      const res = await orderPrintData(orderId, printType)
      if (res.code === 200 && res.data) {
        return res.data
      }
      this.$message.error(res.message || '获取打印数据失败')
      return null
    },
    /**
     * 模板三级回退：
     * 1) loadTemplateWithFallback(billType, tenantId)——后端自带「租户模板 → 系统模板(tenant_id=0)」两级回退
     * 2) 仍无有效 panels → 组件内置兜底模板（与系统模板同源）
     * @returns {Promise<Object|null>} 模板 JSON 对象（hiprint 结构）
     */
    async loadTemplate(billTypeCode) {
      try {
        const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
        const tenantId = userInfo ? userInfo.tenantId : 0
        const result = await loadTemplateWithFallback(billTypeCode, tenantId)
        if (result && result.templateJson && result.templateJson.panels && result.templateJson.panels.length) {
          this.templateId = result.id || null
          return result.templateJson
        }
      } catch (error) {
        console.warn('[DiningPrintModal] 模板加载失败，使用内置兜底模板:', error)
      }
      this.templateId = null
      return FALLBACK_TEMPLATES[billTypeCode] || null
    },
    /**
     * 模板明细表格 field 为 table，补 items → table 别名
     */
    buildRenderData(data) {
      return Object.assign({}, data, { table: data.items || [] })
    },
    /**
     * 渲染小票预览到弹窗内容区
     */
    renderPreview() {
      const el = this.$refs.printContent
      if (!el || !this.templateJson) return
      try {
        if (HiprintService.isAvailable()) {
          el.innerHTML = HiprintService.renderHtml(this.templateJson, this.renderData || {})
        } else {
          console.warn('[DiningPrintModal] hiprint 未初始化，无法渲染预览')
          el.innerHTML = ''
        }
      } catch (error) {
        console.error('[DiningPrintModal] 渲染预览失败:', error)
        el.innerHTML = ''
      }
    },
    /**
     * 打印：通过 styleHandler 注入 @page 纸张尺寸（panel 宽高）。
     * 0.0.60 的 template.print() 不携带模板纸张尺寸，不注入则打印走驱动默认 A4 纸，
     * 58mm 热敏打印机会整页空白（A4 打印机正常出字即为该症状）。
     */
    doHiprintPrint(templateJson, data) {
      const template = HiprintService.createTemplate(templateJson)
      const panel = templateJson && templateJson.panels && templateJson.panels[0]
      const options = {}
      if (panel && panel.width && panel.height) {
        const css = '@page{size:' + panel.width + 'mm ' + panel.height + 'mm;margin:0}'
        options.styleHandler = function() {
          return '<style>' + css + '</style>'
        }
      }
      template.print(data, {}, options)
    },
    /**
     * 打印（直打后端通道）：receiptPrint → JZD/YJD → 联次路由 → 收银台小票机出票
     */
    handlePrint() {
      if (!this.orderId || !this.printType) return
      this.printSilent(this.orderId, this.printType)
    },
    /**
     * 预览打印：浏览器 hiprint 面板（对账/无小票机场景用）
     */
    handlePreviewPrint() {
      if (!this.templateJson || !this.printData) return
      try {
        this.doHiprintPrint(this.templateJson, this.buildRenderData(this.printData))
      } catch (error) {
        console.error('[DiningPrintModal] 打印失败:', error)
        this.$message.error('打印失败，请重试')
      }
    },
    handleTemplateSetting() {
      this.designerVisible = true
    },
    onDesignerClose() {
      this.designerVisible = false
    },
    /**
     * 设计器保存成功 → 重新走模板回退链加载 + 重渲染预览
     */
    async onDesignerSaved() {
      this.designerVisible = false
      this.$message.success('模板保存成功，正在刷新预览...')
      this.loading = true
      try {
        this.templateJson = await this.loadTemplate(this.billTypeCode)
        this.$nextTick(() => this.renderPreview())
      } finally {
        this.loading = false
      }
    },
    handleCancel() {
      this.visible = false
      this.designerVisible = false
    }
  }
}

/**
 * 读取主框架系统配置 autoPrint（getCurrentSystemInfo）：
 * '1' → 自动打印开启（收银出票静默打印），供各接入点分流调用
 * @returns {Promise<boolean>}
 */
PrintModal.isAutoPrintEnabled = async function() {
  try {
    const res = await getCurrentSystemInfo()
    return !!(res && res.code === 200 && res.data && res.data.autoPrint === '1')
  } catch (error) {
    console.warn('[DiningPrintModal] 读取自动打印配置失败:', error)
    return false
  }
}

export default PrintModal
</script>

<style scoped>
.print-toolbar {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.preview-area {
  max-height: calc(100vh - 260px);
  overflow: auto;
  background: #f5f5f5;
  padding: 16px;
}

.print-content {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

.designer-area {
  height: calc(100vh - 180px);
  min-height: 560px;
}
</style>
